import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api.js';
import { addDays, isoDate } from '../../lib/time.js';
import { sanitizeShape } from '../../lib/sessionState.js';
import { normalizeWorkFilter } from '../../lib/workTypes.js';
import { useSessionState } from '../../lib/useSessionState.js';
import {
  filterProjectsByClients,
  filterUsersByTeams,
  keepVisibleSelections,
  monthStart,
  toggleValue,
} from './reportUtils.js';

export const emptyReport = {
  summary: {
    entryCount: 0,
    totalHours: 0,
    billableHours: 0,
    billablePct: 0,
    workHours: 0,
    internalHours: 0,
    paidAbsenceHours: 0,
    unpaidAbsenceHours: 0,
    absenceHours: 0,
    workTypeHours: null,
    projectCount: 0,
    userCount: 0,
    clientCount: 0,
  },
  projects: [],
  people: [],
  clients: [],
  entries: [],
};

// The range presets offered by the date range picker, in display order.
export const RANGE_PRESETS = [
  { key: 'this-week', label: 'This week' },
  { key: 'last-week', label: 'Last week' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'this-year', label: 'This year' },
  { key: 'last-year', label: 'Last year' },
  { key: 'all', label: 'All time' },
];
const datePresets = RANGE_PRESETS.map((preset) => preset.key);

export function presetRange(preset, now = new Date()) {
  if (preset === 'all') {
    return {
      from: isoDate(
        new Date(now.getFullYear() - 10, now.getMonth(), now.getDate()),
      ),
      to: isoDate(now),
    };
  }
  if (preset === 'this-week') {
    const day = now.getDay() || 7;
    const start = addDays(now, 1 - day);
    return { from: isoDate(start), to: isoDate(addDays(start, 6)) };
  }
  if (preset === 'last-week') {
    const day = now.getDay() || 7;
    const start = addDays(now, 1 - day - 7);
    return { from: isoDate(start), to: isoDate(addDays(start, 6)) };
  }
  if (preset === 'this-month') {
    return {
      from: monthStart(now),
      to: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }
  if (preset === 'last-month') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: isoDate(start), to: isoDate(end) };
  }
  if (preset === 'this-year') {
    return {
      from: isoDate(new Date(now.getFullYear(), 0, 1)),
      to: isoDate(new Date(now.getFullYear(), 11, 31)),
    };
  }
  if (preset === 'last-year') {
    return {
      from: isoDate(new Date(now.getFullYear() - 1, 0, 1)),
      to: isoDate(new Date(now.getFullYear() - 1, 11, 31)),
    };
  }
  return null;
}

export function buildReportQuery({
  mode,
  from,
  to,
  billing,
  clientIds,
  projectIds,
  teamIds,
  userIds,
  scopeClientId,
  scopeProjectId,
  scopeUserId,
  scopeTeamIds,
}) {
  const effectiveClientIds = scopeClientId ? [scopeClientId] : clientIds;
  const effectiveProjectIds = scopeProjectId ? [scopeProjectId] : projectIds;
  const effectiveUserIds = scopeUserId ? [scopeUserId] : userIds;
  // Unlike the single-entity scopes above, a team scope is a floor rather
  // than a fixed value: it decides what "no selection" means (a team
  // manager's screen starts at the teams they manage), and a selection
  // narrows within it.
  const effectiveTeamIds = teamIds.length ? teamIds : scopeTeamIds || [];
  const params = new URLSearchParams({ mode, from, to });
  const workTypes = normalizeWorkFilter(billing);
  if (workTypes && workTypes !== 'all') params.set('workTypes', workTypes);
  if (effectiveClientIds.length)
    params.set('clientIds', effectiveClientIds.join(','));
  if (effectiveProjectIds.length)
    params.set('projectIds', effectiveProjectIds.join(','));
  if (mode !== 'mine' && effectiveTeamIds.length)
    params.set('teamIds', effectiveTeamIds.join(','));
  if (mode !== 'mine' && effectiveUserIds.length)
    params.set('userIds', effectiveUserIds.join(','));
  return params.toString();
}

export function defaultRange(now = new Date()) {
  return {
    from: monthStart(now),
    to: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
}

// The date range + work type are shared app-wide (provided by AppShell), so
// the period a manager picks on Team/Reports survives drilling into a member,
// client or project detail instead of resetting to "this month" on each mount.
// Without a provider (tests, standalone use) the hook falls back to local
// state with the same defaults.
export const ReportRangeContext = createContext(null);

const NO_SELECTION = {
  clientIds: [],
  projectIds: [],
  teamIds: [],
  userIds: [],
};

// How many filters differ from the defaults — what "Reset filters" shows.
// The date range is not counted: it is a period, not a filter, and Reset
// leaves it alone.
export function activeFilterCount({
  billing,
  clientIds,
  projectIds,
  teamIds,
  userIds,
}) {
  return (
    (billing && billing !== 'all' ? 1 : 0) +
    clientIds.length +
    projectIds.length +
    teamIds.length +
    userIds.length
  );
}

// Shared state for the report filter panel (date range, work type and the
// client/project/team/people checkbox groups). Returns exactly the props
// `ReportFilters` expects, so callers can spread the result into it, plus the
// `/reports/data` query string for `useReportData`. The date range and work
// type come from `ReportRangeContext` when a provider is mounted (the app
// shell), so they carry across screens; the checkbox groups are per-screen
// and, given a `persistKey`, remembered for the tab session (sessionState.js)
// so they survive leaving the screen and coming back.
export function useReportFilters(
  data,
  { mode = 'all', scope = {}, persistKey = null } = {},
) {
  const today = new Date();
  const shared = useContext(ReportRangeContext);
  const initial = defaultRange(today);
  const [localFrom, setLocalFrom] = useState(initial.from);
  const [localTo, setLocalTo] = useState(initial.to);
  const [localBilling, setLocalBilling] = useState('all');
  const from = shared ? shared.from : localFrom;
  const to = shared ? shared.to : localTo;
  const billing = shared ? shared.billing : localBilling;
  const setFrom = shared ? shared.setFrom : setLocalFrom;
  const setTo = shared ? shared.setTo : setLocalTo;
  const setBilling = shared ? shared.setBilling : setLocalBilling;
  const [stored, setStored] = useSessionState(
    persistKey ? `report.${persistKey}` : null,
    NO_SELECTION,
    sanitizeShape,
  );
  // A remembered id the caller can no longer see (a deleted client, a lost
  // permission) is dropped rather than silently filtering to nothing. Projects
  // and people are also checked against the kept clients/teams, like the
  // toggles below do: one that moved to an unselected client or team would be
  // missing from its control yet still narrow the report to nothing.
  const clientIds = useMemo(
    () => keepVisibleSelections(stored.clientIds, data.clients),
    [stored.clientIds, data.clients],
  );
  const projectIds = useMemo(
    () =>
      keepVisibleSelections(
        stored.projectIds,
        filterProjectsByClients(data.allProjects, clientIds),
      ),
    [stored.projectIds, data.allProjects, clientIds],
  );
  const teamIds = useMemo(
    () => keepVisibleSelections(stored.teamIds, data.teams || []),
    [stored.teamIds, data.teams],
  );
  const userIds = useMemo(
    () =>
      keepVisibleSelections(
        stored.userIds,
        filterUsersByTeams(data.users, teamIds),
      ),
    [stored.userIds, data.users, teamIds],
  );
  const select = (patch) => setStored((current) => ({ ...current, ...patch }));
  const {
    clientId: scopeClientId,
    projectId: scopeProjectId,
    userId: scopeUserId,
    teamIds: scopeTeamIds,
  } = scope;

  const query = useMemo(
    () =>
      buildReportQuery({
        mode,
        from,
        to,
        billing,
        clientIds,
        projectIds,
        teamIds,
        userIds,
        scopeClientId,
        scopeProjectId,
        scopeUserId,
        scopeTeamIds,
      }),
    [
      mode,
      from,
      to,
      billing,
      clientIds,
      projectIds,
      teamIds,
      userIds,
      scopeClientId,
      scopeProjectId,
      scopeUserId,
      scopeTeamIds,
    ],
  );

  const activePreset =
    datePresets.find((preset) => {
      const range = presetRange(preset, today);
      return range?.from === from && range?.to === to;
    }) || '';

  function onPreset(preset) {
    const range = presetRange(preset);
    if (!range) return;
    setFrom(range.from);
    setTo(range.to);
  }

  // Deselecting a client also drops its projects from the selection.
  function onClientToggle(id) {
    const nextClientIds = toggleValue(clientIds, id);
    select({
      clientIds: nextClientIds,
      projectIds: keepVisibleSelections(
        projectIds,
        filterProjectsByClients(data.allProjects, nextClientIds),
      ),
    });
  }

  // Deselecting a team also drops its people from the selection.
  function onTeamToggle(id) {
    const nextTeamIds = toggleValue(teamIds, id);
    select({
      teamIds: nextTeamIds,
      userIds: keepVisibleSelections(
        userIds,
        filterUsersByTeams(data.users, nextTeamIds),
      ),
    });
  }

  function onClear() {
    setStored(NO_SELECTION);
    setBilling('all');
  }

  return {
    from,
    to,
    billing,
    clientIds,
    projectIds,
    teamIds,
    userIds,
    activePreset,
    activeCount: activeFilterCount({
      billing,
      clientIds,
      projectIds,
      teamIds,
      userIds,
    }),
    query,
    onFromChange: setFrom,
    onToChange: setTo,
    onPreset,
    onBillingChange: setBilling,
    onClientToggle,
    onRangeChange: ({ from: nextFrom, to: nextTo }) => {
      setFrom(nextFrom);
      setTo(nextTo);
    },
    onProjectToggle: (id) =>
      select({ projectIds: toggleValue(projectIds, id) }),
    onTeamToggle,
    onUserToggle: (id) => select({ userIds: toggleValue(userIds, id) }),
    onClear,
  };
}

// Fetches `/reports/data` whenever the filter query changes.
export function useReportData(query) {
  const [report, setReport] = useState(emptyReport);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api(`/reports/data?${query}`)
      .then((nextReport) => {
        if (active) setReport(nextReport);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [query]);

  return { report, loading };
}
